import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import z from 'zod'
import type {
  CommunityVoicesFormValues,
  ProfessionalVoicesFormValues,
  VoiceCloneFormValues,
} from './custom-voice.types'

// Zod Schemas
export const voiceCloneSchema = z.object({
  voiceName: z
    .string()
    .min(1, 'Voice name is required')
    .max(100, 'Voice name must be 100 characters or less'),
  gender: z.enum(['male', 'female'], {
    required_error: 'Gender is required',
  }),
  description: z.string().max(500, 'Description must be 500 characters or less').optional(),
  // Audio file validation: MIME type and size are validated client-side via react-dropzone (10MB max).
  // Duration validation (min 10s) is done client-side in AudioUploadField using AudioContext.decodeAudioData.
  // Server-side validation is performed by ElevenLabs API.
  audioFile: z.any().refine((val) => val instanceof File, {
    message: 'Audio file is required',
  }),
  consentConfirmed: z.boolean().refine((val) => val === true, {
    message: 'You must confirm that you have the right to use this voice',
  }),
})

export const communityVoicesSchema = z.object({
  searchQuery: z.string().min(1, 'Search query is required'),
})

export const professionalVoicesSchema = z.object({
  shareLinkUrl: z
    .string()
    .min(1, 'Share link URL is required')
    .url('Please enter a valid URL')
    .refine((url) => url.includes('elevenlabs'), {
      message: 'Please enter a valid ElevenLabs share link',
    }),
})

// Form field configurations
export const voiceCloneFields: GenericFieldConfig<VoiceCloneFormValues> = [
  {
    name: 'voiceName',
    label: 'Voice Name',
    componentType: 'input',
    zodValidation: voiceCloneSchema.shape.voiceName,
    className: 'col-span-1',
    inputProps: {
      placeholder: 'Enter voice name',
      'data-testid': 'input-voice-name',
    },
  },
  {
    name: 'gender',
    label: 'Gender',
    componentType: 'select',
    zodValidation: voiceCloneSchema.shape.gender,
    className: 'col-span-1',
    inputProps: {
      placeholder: 'Select gender',
      options: [
        { label: 'Male', value: 'male' },
        { label: 'Female', value: 'female' },
      ],
      'data-testid': 'select-gender',
    },
  },
  {
    name: 'description',
    label: 'Description (Optional)',
    componentType: 'textarea',
    zodValidation: voiceCloneSchema.shape.description,
    className: 'col-span-2',
    inputProps: {
      placeholder: 'Describe the voice characteristics',
      rows: 3,
      'data-testid': 'textarea-description',
    },
  },
]

export const communityVoicesFields: GenericFieldConfig<CommunityVoicesFormValues> = [
  {
    name: 'searchQuery',
    label: 'Search',
    componentType: 'input',
    zodValidation: communityVoicesSchema.shape.searchQuery,
    inputProps: {
      placeholder: 'Search by voice name/id',
      'data-testid': 'input-community-search',
    },
  },
]

export const professionalVoicesFields: GenericFieldConfig<ProfessionalVoicesFormValues> = [
  {
    name: 'shareLinkUrl',
    label: 'Share Link URL',
    componentType: 'input',
    zodValidation: professionalVoicesSchema.shape.shareLinkUrl,
    inputProps: {
      inputMode: 'url',
      placeholder: 'Enter the share URL',
      'data-testid': 'input-share-link',
    },
  },
]

// Default values
export const voiceCloneDefaultValues: VoiceCloneFormValues = {
  voiceName: '',
  gender: 'male',
  description: '',
  audioFile: null,
  consentConfirmed: false,
}

export const communityVoicesDefaultValues: CommunityVoicesFormValues = {
  searchQuery: '',
  selectedVoice: null,
}

export const professionalVoicesDefaultValues: ProfessionalVoicesFormValues = {
  shareLinkUrl: '',
}
